import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, CircularProgress, Alert } from '@mui/material';
import { useChowaTheme } from '../config/chowaThemeContext';
import { fetchModelsBySeries, getSeriesColor } from '../model/UnitSeries';

const TimeMonitoringSeriesSelection = ({ selectedSeries, onModelSelect }) => {
  const { clearSeriesColor } = useChowaTheme();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 時間監視選択ページではデフォルトテーマに戻す
  useEffect(() => {
    clearSeriesColor();
  }, [clearSeriesColor]);

  // 選択されたシリーズの機種を取得
  useEffect(() => {
    if (selectedSeries) {
      fetchModelsForSeries(selectedSeries);
    } else {
      setModels([]);
      setError(null);
    }
  }, [selectedSeries]);

  const fetchModelsForSeries = async (seriesKey) => {
    setLoading(true);
    setError(null);
    
    try {
      const modelsData = await fetchModelsBySeries(seriesKey);
      setModels(modelsData);
      
      if (modelsData.length === 0) {
        setError(`${seriesKey}シリーズの機種データが見つかりませんでした。`);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
      setError('機種データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleModelClick = (modelName) => {
    if (onModelSelect) {
      onModelSelect(modelName);
    }
  };

  // シリーズが選択されていない場合
  if (!selectedSeries) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ textAlign: 'center', color: 'text.secondary' }}>
          サイドバーから目的のシリーズを選択してください
        </Typography>
      </Box>
    );
  }

  const seriesColor = getSeriesColor(selectedSeries);

  return (
    <Box sx={{ p: 4 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4, 
          textAlign: 'center',
          color: seriesColor,
          fontWeight: 'bold'
        }}
      >
        {selectedSeries}シリーズ - 機種選択
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: seriesColor }} />
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && models.length > 0 && (
        <>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            機種を選択してデータを取得してください
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {models.map((modelName) => (
              <Grid item key={modelName}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleModelClick(modelName)}
                  sx={{
                    backgroundColor: seriesColor,
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: seriesColor,
                      opacity: 0.8,
                    },
                    minWidth: '180px'
                  }}
                >
                  {modelName}
                </Button>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default TimeMonitoringSeriesSelection; 